import { useState, useCallback, useEffect } from "react";
import { ImageOff, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

interface PhotoCarouselProps {
  photoRefs: string[];
}

function buildPhotoUrl(photoRef: string, size: "thumb" | "full" = "thumb") {
  const maxHeight = size === "full" ? 2400 : 400;
  const maxWidth = size === "full" ? 2400 : 600;
  return `/api/places-photo?ref=${encodeURIComponent(photoRef)}&maxHeight=${maxHeight}&maxWidth=${maxWidth}`;
}

export function PhotoCarousel({ photoRefs }: PhotoCarouselProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!photoRefs.length) return null;

  return (
    <div>
      <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
        Photos
      </h3>
      <div className="flex gap-2 overflow-x-auto scrollbar-none">
        {photoRefs.map((ref, i) => (
          <PhotoThumbnail
            key={ref}
            photoRef={ref}
            index={i}
            onClick={() => setLightboxIndex(i)}
          />
        ))}
      </div>

      <PhotoLightbox
        photoRefs={photoRefs}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
}

function PhotoThumbnail({
  photoRef,
  index,
  onClick,
}: {
  photoRef: string;
  index: number;
  onClick: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = useCallback(() => setLoading(false), []);
  const handleError = useCallback(() => {
    setLoading(false);
    setError(true);
  }, []);

  return (
    <div className="relative size-20 shrink-0 rounded-lg overflow-hidden bg-muted">
      {!error ? (
        <>
          {loading && (
            <Skeleton className="absolute inset-0 size-full rounded-lg" />
          )}
          <img
            src={buildPhotoUrl(photoRef)}
            alt={`Place photo ${index + 1}`}
            className={`size-full object-cover transition-opacity cursor-pointer ${loading ? "opacity-0" : "opacity-100"}`}
            onLoad={handleLoad}
            onError={handleError}
            onClick={onClick}
          />
        </>
      ) : (
        <div className="size-full flex items-center justify-center">
          <ImageOff className="size-5 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

function PhotoLightbox({
  photoRefs,
  index,
  onClose,
  onIndexChange,
}: {
  photoRefs: string[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
  }, [index]);

  useEffect(() => {
    if (index === null) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") {
        onIndexChange(index! > 0 ? index! - 1 : photoRefs.length - 1);
      } else if (e.key === "ArrowRight") {
        onIndexChange(index! < photoRefs.length - 1 ? index! + 1 : 0);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [index, photoRefs.length, onIndexChange]);

  if (index === null) return null;

  const hasPrev = photoRefs.length > 1;
  const hasNext = photoRefs.length > 1;

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent
        showCloseButton={false}
        className="max-w-7xl w-auto p-0 bg-black/95 border-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">
          Photo {index + 1} of {photoRefs.length}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Use arrow keys or buttons to navigate between photos
        </DialogDescription>

        <div className="relative flex items-center justify-center min-h-[400px] max-h-[90vh]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="size-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}

          <img
            src={buildPhotoUrl(photoRefs[index] ?? "", "full")}
            alt={`Place photo ${index + 1}`}
            className={`max-h-[90vh] max-w-full object-contain transition-opacity ${loading ? "opacity-0" : "opacity-100"}`}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 size-8 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
          >
            <X className="size-4" />
          </button>

          {/* Prev / Next */}
          {hasPrev && (
            <button
              onClick={() =>
                onIndexChange(index > 0 ? index - 1 : photoRefs.length - 1)
              }
              className="absolute left-3 top-1/2 -translate-y-1/2 size-10 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            >
              <ChevronLeft className="size-5" />
            </button>
          )}
          {hasNext && (
            <button
              onClick={() =>
                onIndexChange(index < photoRefs.length - 1 ? index + 1 : 0)
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 size-10 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            >
              <ChevronRight className="size-5" />
            </button>
          )}

          {/* Counter */}
          {photoRefs.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
              {index + 1} / {photoRefs.length}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
